/**
 * Passwordless sign-in: enter an email, then the six-digit code sent to it.
 *
 * There is no password field anywhere in the app. A first-time address is
 * registered by its first successful code, so signing up and signing in are
 * the same action -- the user never picks between them.
 */

import { useEffect, useRef, useState } from 'react';
import { emailOtp, signIn } from '../lib/auth-client';

type Step = 'email' | 'code';

const CODE_LENGTH = 6;
/** Matches the server's OTP_TTL_SECONDS. */
const CODE_TTL_SECONDS = 600;
const RESEND_COOLDOWN_SECONDS = 30;

export function AuthPanel({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  /** Null while unknown, so the form is not flashed in and then disabled. */
  const [emailEnabled, setEmailEnabled] = useState<boolean | null>(null);

  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/config')
      .then((r) => r.json() as Promise<{ emailSignInEnabled?: boolean }>)
      .then((cfg) => {
        if (!cancelled) setEmailEnabled(Boolean(cfg.emailSignInEnabled));
      })
      .catch(() => {
        if (!cancelled) setEmailEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const requestCode = async (isResend = false) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await emailOtp.sendVerificationOtp({
        email: email.trim().toLowerCase(),
        type: 'sign-in',
      });
      if (result.error) {
        setError(result.error.message ?? 'Could not send the code. Try again.');
        return;
      }
      setStep('code');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      if (isResend) setNotice('A new code is on its way.');
    } catch {
      setError('Could not reach the sign-in service.');
    } finally {
      setBusy(false);
    }
  };

  /** Takes the digits explicitly so the autosubmit path does not race setState. */
  const verifyCode = async (digits: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await signIn.emailOtp({
        email: email.trim().toLowerCase(),
        otp: digits,
      });
      if (result.error) {
        setError(messageFor(result.error.code, result.error.message));
        // A burned code is not worth retyping -- send them back to the start.
        if (result.error.code === 'TOO_MANY_ATTEMPTS') {
          setStep('email');
          setCode('');
        }
        return;
      }
      onClose();
    } catch {
      setError('Could not reach the sign-in service.');
    } finally {
      setBusy(false);
    }
  };

  const emailLooksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const codeComplete = code.trim().length === CODE_LENGTH;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-head">
          <h2>{step === 'email' ? 'Sign in' : 'Enter your code'}</h2>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {emailEnabled === false && (
          <p className="small" role="alert" style={{ color: 'var(--warn)', marginBottom: 14 }}>
            Email sign-in is not configured for this deployment yet, so codes
            cannot be delivered. You can still join a game as a guest from an
            invite link.
          </p>
        )}

        {step === 'email' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (emailLooksValid) void requestCode();
            }}
          >
            <p className="small muted" style={{ marginBottom: 14 }}>
              We&rsquo;ll email you a {CODE_LENGTH}-digit code. No password needed.
            </p>
            <div className="field">
              <label htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <p className="small" role="alert" style={{ color: 'var(--danger)', marginBottom: 12 }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary"
              style={{ width: '100%' }}
              disabled={busy || !emailLooksValid || emailEnabled === false}
            >
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (codeComplete) void verifyCode(code.trim());
            }}
          >
            <p className="small muted" style={{ marginBottom: 14 }}>
              Sent to <strong>{email.trim().toLowerCase()}</strong>. It expires in{' '}
              {Math.round(CODE_TTL_SECONDS / 60)} minutes.
            </p>
            <div className="field">
              <label htmlFor="auth-code">{CODE_LENGTH}-digit code</label>
              <input
                ref={codeRef}
                id="auth-code"
                // Not type="number": that strips leading zeros and shows spinners.
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={CODE_LENGTH}
                placeholder="000000"
                style={{
                  fontSize: 26,
                  letterSpacing: '0.36em',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                }}
                value={code}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                  setCode(digits);
                  // Autosubmit on the sixth digit -- the usual expectation, and
                  // it saves a tap on mobile after an SMS/email autofill.
                  if (digits.length === CODE_LENGTH) void verifyCode(digits);
                }}
              />
            </div>

            {error && (
              <p className="small" role="alert" style={{ color: 'var(--danger)', marginBottom: 12 }}>
                {error}
              </p>
            )}
            {notice && (
              <p className="small" role="status" style={{ color: 'var(--win)', marginBottom: 12 }}>
                {notice}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary"
              style={{ width: '100%' }}
              disabled={busy || !codeComplete}
            >
              {busy ? 'Checking…' : 'Sign in'}
            </button>

            <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
              <button
                type="button"
                className="btn-ghost small"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                  setNotice(null);
                }}
              >
                ← Use a different email
              </button>
              <button
                type="button"
                className="btn-ghost small"
                disabled={busy || cooldown > 0}
                onClick={() => void requestCode(true)}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Better Auth's OTP error codes, in language a person can act on. */
function messageFor(code: string | undefined, fallback: string | undefined): string {
  switch (code) {
    case 'INVALID_OTP':
      return 'That code is not right. Check it and try again.';
    case 'OTP_EXPIRED':
      return 'That code has expired. Request a new one.';
    case 'TOO_MANY_ATTEMPTS':
      return 'Too many tries. Request a fresh code.';
    default:
      return fallback ?? 'Sign-in failed. Try again.';
  }
}
